import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    LOCALE_ID,
    provideAppInitializer,
    provideZonelessChangeDetection
} from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { provideRouter } from "@angular/router";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideNiceFilterViewTranslater } from "@recursyve/nice-filter-view/common";
import * as Sentry from "@sentry/angular";
import { Locale } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
import { environment } from "../environments/environment";
import { authInterceptor } from "./api/interceptors/auth.interceptor";
import { languageInterceptor } from "./api/interceptors/language.interceptor";
import { ApiConfiguration } from "./api/nice-ops/api-configuration";
import { routes } from "./app.routes";
import { TranslocoHttpLoader } from "./core/transloco/transloco-loader";
import { TranslocoPreloaderService } from "./core/transloco/transloco-preloader.service";

const languageMap: Record<string, Locale> = {
    fr: frCA,
    en: enCA
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(routes),
        provideHttpClient(withInterceptors([authInterceptor, languageInterceptor])),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),

        provideDateFnsAdapter(),

        { provide: LOCALE_ID, useFactory: () => languageMap["fr"].code },

        {
            provide: MAT_DATE_LOCALE,
            useFactory: () => languageMap["fr"]
        },

        // Necessary to make sure all translations are loaded before the app starts.
        // Otherwise there is an flaky issue where the translations for the filters don't work.
        provideAppInitializer(() => {
            const service = inject(TranslocoPreloaderService);
            return service.preload();
        }),

        provideTransloco({
            config: {
                availableLangs: ["en", "fr"],
                defaultLang: "fr",
                reRenderOnLangChange: false,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),

        provideNiceFilterViewTranslater({
            translater: {
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params),
                deps: [TranslocoService]
            }
        }),

        {
            provide: ApiConfiguration,
            useFactory: () => {
                const config = new ApiConfiguration();
                config.rootUrl = environment.apiUrl;
                return config;
            }
        },

        // Sentry Error Handler
        ...(environment.sentry.enabled
            ? [
                  {
                      provide: ErrorHandler,
                      useValue: Sentry.createErrorHandler({
                          showDialog: false,
                          logErrors: true
                      })
                  }
              ]
            : [])
    ]
};
